/**
 * Ticket-type selection rule (`event.ticketTypeSelection`, nimi-backend #1386 / nimi-app #1556).
 *
 * Mirrors `validateTicketTypeSelection` in the backend `@nimee/calculations` lib so the picker,
 * the dashboard warnings and the link form count exactly what the order route enforces:
 * - only regular tickets count (`type` "ticket" or missing — most prod tickets have no type);
 * - seats events are never limited;
 * - an order with zero regular tickets is never limited (free-ticket links, group captures);
 * - scope "order" (default, also when missing): min/max distinct regular types in the order;
 * - scope "group": max only, checked separately inside every group the order touches; a
 *   `selectionGroupId` that is not in `groups` counts as ungrouped, and ungrouped tickets are
 *   never limited.
 */

export const SELECTION_SCOPE = { ORDER: 'order', GROUP: 'group' };

export const isRegularTicket = (ticket) => ticket?.type == null || ticket.type === 'ticket';

export const getSelectionRule = (event) => event?.ticketTypeSelection || null;

export const getSelectionScope = (event) =>
    getSelectionRule(event)?.scope === SELECTION_SCOPE.GROUP ? SELECTION_SCOPE.GROUP : SELECTION_SCOPE.ORDER;

/** The rule is on and can apply to this event (seats events bypass it). */
export const isSelectionRuleActive = (event) => Boolean(getSelectionRule(event)?.enabled) && !event?.isSeatsEvent;

export const isGroupScopeActive = (event) =>
    isSelectionRuleActive(event) && getSelectionScope(event) === SELECTION_SCOPE.GROUP;

const toLimit = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
};

export const getMinTypes = (event) => toLimit(getSelectionRule(event)?.minTypes);

export const getMaxTypes = (event) => toLimit(getSelectionRule(event)?.maxTypes);

export const getSelectionGroups = (event) => {
    const groups = getSelectionRule(event)?.groups;
    return Array.isArray(groups) ? groups.filter((g) => g && g.id) : [];
};

const chosenAmount = (chosenTickets, ticketId) => Number(chosenTickets?.[ticketId]?.amount ?? 0) || 0;

/** Regular tickets with a chosen quantity of at least 1. */
export const getSelectedRegularTickets = (tickets = [], chosenTickets = {}) =>
    tickets.filter((t) => isRegularTicket(t) && chosenAmount(chosenTickets, t._id) >= 1);

export const countSelectedRegular = (tickets = [], chosenTickets = {}) =>
    new Set(getSelectedRegularTickets(tickets, chosenTickets).map((t) => String(t._id))).size;

/** Group id of a ticket, or null when it is ungrouped or points at a group that no longer exists. */
export const resolveTicketGroupId = (event, ticket) => {
    const id = ticket?.selectionGroupId;
    if (!id) return null;
    return getSelectionGroups(event).some((g) => g.id === id) ? id : null;
};

/** Name of the ticket's group, or null (used for the checkout prefix). */
export const resolveTicketGroupName = (event, ticket) => {
    const id = resolveTicketGroupId(event, ticket);
    if (!id) return null;
    return getSelectionGroups(event).find((g) => g.id === id)?.name || null;
};

/**
 * Buckets tickets into sections in `groups` order; ungrouped tickets (and unknown ids) come last
 * with `group: null`. Ticket order inside a section is preserved. Empty sections are dropped.
 * Non-regular tickets (e.g. a SystemFreeTicket on a link) go to the ungrouped section.
 */
export const bucketTicketsByGroup = (event, tickets = []) => {
    const groups = getSelectionGroups(event);
    const byId = new Map(groups.map((g) => [g.id, []]));
    const ungrouped = [];
    tickets.forEach((t) => {
        const id = isRegularTicket(t) ? resolveTicketGroupId(event, t) : null;
        if (id) byId.get(id).push(t);
        else ungrouped.push(t);
    });
    const sections = groups
        .map((g) => ({ group: g, tickets: byId.get(g.id) }))
        .filter((s) => s.tickets.length > 0);
    if (ungrouped.length) sections.push({ group: null, tickets: ungrouped });
    return sections;
};

/** G9: max 1 and every ticket of the group allows at most 1 per order → radio-like picking. */
export const isTapToChooseGroup = (event, groupTickets = []) =>
    getMaxTypes(event) === 1 &&
    groupTickets.length > 0 &&
    groupTickets.every((t) => Number(t.countTicketsAllowPerPurchase) <= 1);

/**
 * Same verdict as the backend helper, for the current selection.
 * Returns `{ ok: true }` or
 *   `{ ok: false, scope: 'order', selected, minTypes, maxTypes }` /
 *   `{ ok: false, scope: 'group', groupId, groupName, selected, maxTypes }`.
 */
export const validateTicketTypeSelection = (event, tickets = [], chosenTickets = {}) => {
    if (!isSelectionRuleActive(event)) return { ok: true };
    const selected = getSelectedRegularTickets(tickets, chosenTickets);
    if (selected.length === 0) return { ok: true };
    const maxTypes = getMaxTypes(event);

    if (getSelectionScope(event) === SELECTION_SCOPE.GROUP) {
        if (maxTypes == null) return { ok: true };
        for (const group of getSelectionGroups(event)) {
            const ids = new Set(selected.filter((t) => resolveTicketGroupId(event, t) === group.id).map((t) => String(t._id)));
            if (ids.size > maxTypes) {
                return { ok: false, scope: SELECTION_SCOPE.GROUP, groupId: group.id, groupName: group.name, selected: ids.size, maxTypes };
            }
        }
        return { ok: true };
    }

    const minTypes = getMinTypes(event);
    const count = new Set(selected.map((t) => String(t._id))).size;
    if ((minTypes != null && count < minTypes) || (maxTypes != null && count > maxTypes)) {
        return { ok: false, scope: SELECTION_SCOPE.ORDER, selected: count, minTypes, maxTypes };
    }
    return { ok: true };
};

/**
 * Order-scope state for the public picker (spec §6.2). Pure so the picker and the tests share it.
 *
 * @param {object} event
 * @param {object[]} tickets           tickets visible on the page (after link / sale-window filters)
 * @param {object} chosenTickets       `{ [ticketId]: { amount } }`
 * @param {object} [opts]
 * @param {number} [opts.purchasableRegularCount]  regular types that can still be bought (defaults
 *                                                 to every visible regular type)
 * @param {number} [opts.capacityLeft]  seats left for the whole order (event capacity / slot)
 */
export const getOrderScopePickerState = (event, tickets = [], chosenTickets = {}, opts = {}) => {
    const active = isSelectionRuleActive(event) && getSelectionScope(event) === SELECTION_SCOPE.ORDER;
    const minTypes = getMinTypes(event);
    const maxTypes = getMaxTypes(event);
    const visibleRegular = tickets.filter(isRegularTicket).length;
    const selected = countSelectedRegular(tickets, chosenTickets);
    // Banner only when the rule can mean something here: a free-ticket link that lists only the
    // SystemFreeTicket must not show an unsatisfiable "0 of 3–4" (zero-regular carve-out).
    const showBanner = active && visibleRegular >= 1 && (minTypes != null || maxTypes != null);
    const ruleApplies = active && selected >= 1;
    const belowMin = minTypes != null && selected < minTypes;
    const aboveMax = maxTypes != null && selected > maxTypes;
    const purchasable = opts.purchasableRegularCount ?? visibleRegular;
    const capacityLeft = opts.capacityLeft ?? Infinity;
    const unsatisfiable = showBanner && minTypes != null && (purchasable < minTypes || capacityLeft < minTypes);
    return {
        active,
        showBanner,
        minTypes,
        maxTypes,
        selected,
        inRange: !belowMin && !aboveMax,
        ruleApplies,
        belowMin,
        missing: belowMin ? minTypes - selected : 0,
        // Disable-at-max applies once a regular type is selected.
        atMax: ruleApplies && maxTypes != null && selected >= maxTypes,
        unsatisfiable,
        // Rule-wise permission to continue; the caller still requires hasChosenTickets.
        blocksProceed: ruleApplies && (belowMin || aboveMax || unsatisfiable),
    };
};

/**
 * Per-section state for the group-scope picker (spec §11.7). `section` is one entry of
 * `bucketTicketsByGroup`; the ungrouped section (`group: null`) is never limited.
 */
export const getGroupSectionState = (event, section, chosenTickets = {}) => {
    const maxTypes = getMaxTypes(event);
    const limited = Boolean(section?.group) && maxTypes != null;
    const selected = countSelectedRegular(section?.tickets || [], chosenTickets);
    return {
        limited,
        maxTypes,
        selected,
        atMax: limited && selected >= maxTypes,
        tapToChoose: limited && isTapToChooseGroup(event, section.tickets),
    };
};

/**
 * True when `name` already contains `part` as a standalone token (not "סבב 1" inside "סבב 10"):
 * the characters around the match must not be digits.
 */
const containsToken = (name, part) => {
    if (!part) return false;
    let from = 0;
    while (from <= name.length) {
        const at = name.indexOf(part, from);
        if (at === -1) return false;
        const before = name[at - 1];
        const after = name[at + part.length];
        if (!/\d/.test(before || '') && !/\d/.test(after || '')) return true;
        from = at + 1;
    }
    return false;
};

/**
 * Display label for a ticket in checkout: "{group} · {name}" in group scope (G8), else the name.
 * The prefix is skipped when the ticket name already carries the group — the whole group name, or
 * its first " · " segment (tickets named "סבב 1 · 11:30 | יוגה" in group "סבב 1 · 11:30–12:15").
 */
export const getTicketLabelWithGroup = (event, ticket, fallbackName) => {
    const name = fallbackName ?? ticket?.name ?? '';
    if (!isGroupScopeActive(event) || !ticket) return name;
    const groupName = String(resolveTicketGroupName(event, ticket) || '').trim();
    if (!groupName) return name;
    const firstSegment = groupName.split(' · ')[0].trim();
    if (containsToken(name, groupName) || containsToken(name, firstSegment)) return name;
    return `${groupName} · ${name}`;
};

/**
 * Splits text into plain parts and numeric/time ranges ("11:30–12:15", "3–4") so the caller can
 * wrap ranges in `<bdi dir="ltr">`: inside Hebrew text a bare range renders reversed.
 * @returns {{ text: string, ltr: boolean }[]}
 */
export const splitLtrRanges = (text) => {
    const source = String(text ?? '');
    // Digits, ":", "/", "." and dashes around the match exclude dates and decimals
    // ("01/01/26-31/12/27", "1.5-2", "1/7-5/7", "2025-11-25") — only bare ranges are isolated.
    const re = /(^|[^\d:/.\u2013\u2014-])(\d{1,2}(?::\d{2})?\s?[–—-]\s?\d{1,2}(?::\d{2})?)(?![\d:/.\u2013\u2014-])/g;
    const parts = [];
    let last = 0;
    let match;
    while ((match = re.exec(source)) !== null) {
        const start = match.index + match[1].length;
        if (start > last) parts.push({ text: source.slice(last, start), ltr: false });
        parts.push({ text: match[2], ltr: true });
        last = start + match[2].length;
    }
    if (last < source.length) parts.push({ text: source.slice(last), ltr: false });
    return parts;
};

/**
 * A link that exposes some regular tickets, but fewer than the order-scope minimum, can never
 * produce a valid order (spec §6.4). A link with zero regular tickets (free-ticket-only) is
 * legitimate: orders with no regular ticket are never limited. Group scope has no minimum.
 * Used by the link form and the selection-limit panel; pass the live rule as `event` there.
 */
export const linkExposesTooFewTypes = (event, linkTickets = []) => {
    if (!isSelectionRuleActive(event) || getSelectionScope(event) !== SELECTION_SCOPE.ORDER) return false;
    const minTypes = getMinTypes(event);
    if (minTypes == null) return false;
    const regular = new Set((linkTickets || []).filter((t) => t && isRegularTicket(t)).map((t) => String(t._id ?? t.name))).size;
    return regular > 0 && regular < minTypes;
};
